import Debug from 'debug'

const log = Debug('gg:api')

const AUTH_TOKEN_KEY = 'auth-token-v4'
const DEFAULT_BASE_URL = '/api'

export interface ApiResponse<T> {
  data: T
  ok: boolean
  status: number
}

export interface ApiError {
  message: string
  status: number
  details?: unknown
}

function getToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_KEY)
}

function clearToken() {
  localStorage.removeItem(AUTH_TOKEN_KEY)
}

function buildHeaders(includeAuth: boolean): HeadersInit {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  }

  if (includeAuth) {
    const token = getToken()
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }
  }

  return headers
}

function buildUrl(path: string, baseUrl: string): string {
  const cleanBase = baseUrl.replace(/\/$/, '')
  const cleanPath = path.startsWith('/') ? path : `/${path}`
  return `${cleanBase}${cleanPath}`
}

async function handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
  if (response.status === 401) {
    log('401 unauthorized - clearing token and redirecting to login')
    clearToken()
    window.location.href = '/login'
    throw createApiError('Unauthorized - session expired', 401)
  }

  if (response.status === 204) {
    return { data: null as T, ok: true, status: 204 }
  }

  if (!response.ok) {
    let details: unknown
    try {
      details = await response.json()
    } catch {
      // response body wasn't json, that's fine
    }
    const message = `API error: ${response.status} ${response.statusText}`
    log('%s %s', message, details)
    throw createApiError(message, response.status, details)
  }

  const data = (await response.json()) as T
  return { data, ok: true, status: response.status }
}

function createApiError(message: string, status: number, details?: unknown): ApiError {
  return { message, status, details }
}

export function isApiError(error: unknown): error is ApiError {
  return typeof error === 'object' && error !== null && 'message' in error && 'status' in error
}

function createApiClient(baseUrl = DEFAULT_BASE_URL) {
  async function request<T>(method: string, path: string, body?: unknown, includeAuth = true): Promise<ApiResponse<T>> {
    const url = buildUrl(path, baseUrl)
    log('%s %s', method, url)

    const response = await fetch(url, {
      method,
      headers: buildHeaders(includeAuth),
      credentials: 'same-origin',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })

    return handleResponse<T>(response)
  }

  return {
    get<T>(path: string, includeAuth = true) {
      return request<T>('GET', path, undefined, includeAuth)
    },

    post<T>(path: string, body: unknown, includeAuth = true) {
      return request<T>('POST', path, body, includeAuth)
    },

    put<T>(path: string, body: unknown, includeAuth = true) {
      return request<T>('PUT', path, body, includeAuth)
    },

    delete<T>(path: string, body?: unknown, includeAuth = true) {
      return request<T>('DELETE', path, body, includeAuth)
    },
  }
}

export const apiClient = createApiClient()
